import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Dropdown from "@radix-ui/react-dropdown-menu";
import {
  CloudDownload,
  History,
  Loader2,
  Locate,
  RefreshCcw,
} from "lucide-react";
import { toast } from "sonner";
import {
  MAX_SYNC_BACKFILL_DAYS,
  type AdminSyncTriplinkLastRun,
} from "@mtform/shared";
import { ApiError, api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useConfirm } from "@/components/ui/confirm-dialog";

// =============================================================================
// TripLink / TMS sync controls
//
// These are INTERNAL operator actions for the Royal Transportation team
// (manual pull + reconcile, one-time historical backfill) — never for
// Mount Sinai staff. They used to sit in the Bookings page toolbar,
// where they only confused regular admins; they now live in the
// top-right account dropdown alongside "TMS Cleanup", gated to the same
// Royal-team audience. The 5-minute scheduled sync inside the API keeps
// the local `bookings` table fresh on its own; these controls are for
// the cases where an operator wants to refresh before the next tick, or
// kick off the historical backfill on a fresh deploy.
//
// Split into two pieces so the move into the dropdown is correct:
//
//   * `useTriplinkSync` owns the mutations + confirm flow. It's called
//     from <AccountMenu>, which stays mounted whether or not the
//     dropdown is open — so a Backfill confirm (which closes the menu by
//     stealing focus) doesn't unmount the mutation mid-flight.
//   * `SyncTriplinkMenuSection` is the presentational dropdown section
//     rendered inside <Dropdown.Content>; it reads the hook's state.
//
// A LIVE in-progress indicator lives here too. The TripLink sync runs
// under a single-flight Postgres advisory lock, so when a pass is already
// going (e.g. the 5-minute scheduler tick, or another admin's run) a
// click on "Sync now"/"Backfill" can't start a second pass — it just
// bounced off a bare "another sync is already running" toast with no
// visual cue. The hook now polls `GET /api/admin/sync-triplink/status`
// (which reports the SHARED lock state, not just locally-initiated runs)
// while the dropdown is open or a local run is in flight, shows a
// "Sync in progress…" spinner, disables the lock-bound actions while a
// pass runs, and surfaces the last finished pass's result line. react-
// query owns the poll interval, so there's no manual tick to leak.
//
// Long-term sync freshness/health still also lives on the Admin Reports
// "TMS Sync" KPI tile (a separate component fed by the reports
// dashboard's `syncHealth`); this indicator is the at-the-moment view.
// =============================================================================

/** react-query key for the sync-status poll. Shared so the mutations can
 *  invalidate it the moment a run settles (or bounces off the lock). */
const SYNC_STATUS_KEY = ["admin", "sync-triplink", "status"] as const;

/** Default backfill window — the old fixed "Backfill 60d" value, kept as
 *  the sensible starting point for the custom-days input. */
const DEFAULT_BACKFILL_DAYS = 60;

/** Quick-preset chips next to the custom-days input. Convenience only —
 *  picking one just fills the input; Backfill still confirms before running. */
const BACKFILL_PRESETS = [30, 60, 90] as const;

interface UseTriplinkSyncOptions {
  /** Bump `["admin", "bookings"]` after a successful sync so a Bookings
   *  list open elsewhere picks up newly-synced rows immediately. */
  invalidateOnSuccess?: boolean;
  /** Poll the sync-status endpoint while truthy — set to the account
   *  dropdown's open state (AND Royal-team membership, so non-Royal
   *  admins never hit the Royal-gated endpoint). Polling also stays
   *  alive while a locally-initiated run is in flight even after the
   *  menu closes, since a Backfill confirm closes the dropdown. */
  pollStatus?: boolean;
}

export interface TriplinkSyncState {
  /** A manual sync or backfill THIS client started is in flight. */
  isPending: boolean;
  /** A sync pass is running RIGHT NOW — this client's run, the 5-minute
   *  scheduler on any instance, or another admin's run. Drives the
   *  "Sync in progress…" indicator and the busy state on the lock-bound
   *  actions. Reads the shared advisory lock via the status poll. */
  isSyncRunning: boolean;
  /** Most recent finished pass's summary (counts + outcome), or null when
   *  none has completed yet / the status hasn't loaded. Powers the
   *  "Last sync …" result line. */
  lastRun: AdminSyncTriplinkLastRun | null;
  /** Pull the latest schedule from TMS (default window: today + 14d). */
  syncNow: () => void;
  /** Confirm + run a one-time historical backfill over a custom window
   *  (`days` back from today, heavier than the default sync). */
  backfill: (days: number) => void;
  /** Re-sync a single trip by friendly id (the operator "Re-sync trip"
   *  action). Reconciles just that trip so a late TripLink service-type /
   *  mileage / status correction lands without a full pass. */
  resyncTrip: (friendlyId: string) => void;
  /** A single-trip re-sync is in flight. */
  isResyncing: boolean;
}

export function useTriplinkSync({
  invalidateOnSuccess = true,
  pollStatus = false,
}: UseTriplinkSyncOptions = {}): TriplinkSyncState {
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const syncMutation = useMutation({
    mutationFn: (vars: {
      days?: number;
      backwardDays?: number;
      forwardDays?: number;
    }) => api.admin.syncTriplink(vars),
    onSuccess: (res, vars) => {
      const { stats } = res;
      const detail =
        stats.failedDates.length > 0
          ? ` — ${stats.failedDates.length} date(s) failed`
          : "";
      const backward = vars.days ?? vars.backwardDays;
      const label = backward && backward > 7 ? "Backfilled" : "Synced";
      toast.success(
        `${label} ${stats.tripsSeen} trip${stats.tripsSeen === 1 ? "" : "s"}` +
          ` · ${stats.inserted} new · ${stats.updated} updated${detail}`,
      );
      if (invalidateOnSuccess) {
        void queryClient.invalidateQueries({ queryKey: ["admin", "bookings"] });
      }
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === "SYNC_IN_PROGRESS") {
        // No bare "try again" anymore — refresh the status so the
        // in-progress indicator lights up and explains the bounce, then
        // point the operator at it.
        void queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
        toast.message(
          "A sync is already running — watch the progress indicator; it'll finish shortly.",
        );
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Sync failed: ${msg}`);
    },
    // A locally-initiated run holds the lock for its whole duration, so by
    // the time it settles the lock is free again — refresh the status line
    // (running flag + last-finished summary) immediately rather than
    // waiting for the next poll tick.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
    },
  });

  // Live sync status. Polls while the dropdown is open OR a local run is
  // in flight; fast (2.5s) while a pass is running, slow (15s) heartbeat
  // otherwise so a scheduler tick that begins while the operator watches
  // still surfaces. Disabled entirely when neither condition holds, so a
  // closed menu does no background work. react-query tears the interval
  // down on disable / unmount — nothing to clean up by hand.
  const pollEnabled = pollStatus || syncMutation.isPending;
  const statusQuery = useQuery({
    queryKey: SYNC_STATUS_KEY,
    queryFn: ({ signal }) => api.admin.syncTriplinkStatus(signal),
    enabled: pollEnabled,
    refetchInterval: (query) => {
      if (!pollEnabled) return false;
      const running = query.state.data?.running ?? false;
      return running || syncMutation.isPending ? 2500 : 15000;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 60_000,
  });

  const isSyncRunning =
    Boolean(statusQuery.data?.running) || syncMutation.isPending;

  const resyncMutation = useMutation({
    mutationFn: (friendlyId: string) => api.admin.resyncTrip({ friendlyId }),
    onSuccess: (res) => {
      if (!res.found) {
        toast.warning(
          `Trip ${res.friendlyId} wasn't found in TripLink's schedule for the dates checked.`,
        );
        return;
      }
      if (res.changes.length === 0) {
        toast.success(`Trip ${res.friendlyId} re-synced — already up to date.`);
      } else {
        const summary = res.changes
          .map((ch) => `${ch.field}: ${formatChangeValue(ch.from)} → ${formatChangeValue(ch.to)}`)
          .join(" · ");
        toast.success(`Trip ${res.friendlyId} updated — ${summary}`);
      }
      if (invalidateOnSuccess) {
        void queryClient.invalidateQueries({ queryKey: ["admin", "bookings"] });
      }
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Re-sync failed: ${msg}`);
    },
  });

  /** Confirm + run a backfill over the operator-chosen window. Backfill
   *  is heavier than the default sync (longer date range = more upstream
   *  calls + more upserts — one feed fetch per date), so we make the
   *  operator confirm explicitly rather than firing it off a single
   *  click. `days` is the custom window picked in the UI; it's already
   *  range-checked client-side and re-validated server-side. */
  async function runBackfill(days: number) {
    const proceed = await confirm({
      title: `Backfill ${days} days?`,
      description: `Pulls every TMS trip from the last ${days} days (plus 14 days forward) and reconciles them into our local cache. Takes a few seconds; runs once.`,
      confirmText: `Backfill ${days} days`,
      cancelText: "Cancel",
    });
    // Re-check after the (async) confirm — a scheduler tick or another
    // admin's run may have grabbed the lock while the dialog was open.
    if (!proceed || syncMutation.isPending || isSyncRunning) return;
    syncMutation.mutate({ days });
  }

  return {
    isPending: syncMutation.isPending,
    isSyncRunning,
    lastRun: statusQuery.data?.lastRun ?? null,
    syncNow: () => {
      // Block a second local kick-off while one is pending, and skip when
      // a pass is already running elsewhere (the lock would reject it
      // anyway) — the indicator explains why.
      if (syncMutation.isPending || isSyncRunning) return;
      syncMutation.mutate({});
    },
    backfill: (days) => {
      if (syncMutation.isPending || isSyncRunning) return;
      void runBackfill(days);
    },
    isResyncing: resyncMutation.isPending,
    resyncTrip: (friendlyId) => {
      const id = friendlyId.trim();
      if (!id || resyncMutation.isPending) return;
      resyncMutation.mutate(id);
    },
  };
}

/** Render an audit-diff value compactly for the success toast — null /
 *  empty render as "∅" so a cleared field is obvious. */
function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "∅";
  return String(value);
}

/** The TripLink-sync section of the account dropdown — a separator, a
 *  muted "internal" header, the two operator actions as Radix menu
 *  items, a live "Sync in progress…" / last-result status line, and the
 *  single-trip re-sync row. Long-term freshness/health still lives on the
 *  Admin Reports "TMS Sync" tile; this is the at-the-moment view.
 *  Rendered inside <Dropdown.Content>; the owning <AccountMenu> calls
 *  `useTriplinkSync` and passes its state in. */
export function SyncTriplinkMenuSection({ sync }: { sync: TriplinkSyncState }) {
  const { isPending, isSyncRunning, lastRun, syncNow, backfill, resyncTrip, isResyncing } =
    sync;
  const [tripId, setTripId] = useState("");
  // Custom backfill window (days back from today). Default 60 — the old
  // fixed "Backfill 60d" value, kept as the sensible starting point.
  const [daysInput, setDaysInput] = useState(String(DEFAULT_BACKFILL_DAYS));

  // The lock-bound actions ("Sync now" / "Backfill") can't start a second
  // pass while one is already running, so disable them whenever a pass is
  // in flight — this client's or anyone's. (The single-trip re-sync runs
  // outside the advisory lock, so it stays governed only by its own
  // in-flight state.)
  const busy = isPending || isSyncRunning;

  // Client-side mirror of the server-side bound (integer 1..cap). An
  // out-of-range / empty value disables Backfill and shows an inline hint;
  // the same ceiling is re-enforced by `adminSyncTriplinkRequestSchema`.
  const parsedDays = Number(daysInput);
  const daysValid =
    daysInput.trim() !== "" &&
    Number.isInteger(parsedDays) &&
    parsedDays >= 1 &&
    parsedDays <= MAX_SYNC_BACKFILL_DAYS;

  function submitResync() {
    const id = tripId.trim();
    if (!id || isResyncing) return;
    resyncTrip(id);
  }

  function submitBackfill() {
    if (!daysValid || busy) return;
    backfill(parsedDays);
  }

  return (
    <>
      <Dropdown.Separator className="my-1 h-px bg-border" />
      <Dropdown.Label className="flex flex-col gap-0.5 px-3 pb-1 pt-1.5">
        <span className="inline-flex items-center gap-1.5 text-[0.7rem] font-semibold uppercase tracking-wider text-muted-foreground">
          <CloudDownload className="h-3.5 w-3.5" aria-hidden />
          TripLink sync · internal
        </span>
      </Dropdown.Label>
      <Dropdown.Item
        disabled={busy}
        // preventDefault keeps the menu open so the operator watches the
        // spinner flip to the success toast instead of the menu vanishing.
        onSelect={(e) => {
          e.preventDefault();
          syncNow();
        }}
        className={cn(
          "flex cursor-default select-none items-center gap-2.5 rounded-md px-3 py-2 text-sm outline-none transition",
          busy
            ? "cursor-not-allowed text-muted-foreground/60"
            : "text-foreground data-[highlighted]:bg-foreground/[0.07]",
        )}
        title="Pull the latest schedule from TMS (default window: today + 14 days)"
      >
        <RefreshCcw
          className={cn("h-4 w-4 text-primary", busy && "animate-spin")}
        />
        <span>
          {isPending ? "Syncing…" : isSyncRunning ? "Sync running…" : "Sync now"}
        </span>
      </Dropdown.Item>
      {/* Custom-days backfill — replaces the old fixed "Backfill 60d".
          The operator picks how many days back to re-scan (a number input
          plus 30/60/90 quick-preset chips), then Backfill confirms + runs
          the regular sync over a wider [today - days, today + 14d]
          window. A plain (non-menu-item) container so the number input is
          typeable: stopPropagation keeps Radix's menu typeahead from
          stealing keystrokes, and there's no onSelect to close the menu on
          a click inside it. The Backfill button itself still opens the
          confirm dialog (which closes the menu by stealing focus) — fine,
          since the hook lives up in <AccountMenu> and survives the unmount. */}
      <div
        className="flex flex-col gap-1.5 px-3 py-2"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 shrink-0 text-primary" aria-hidden />
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_SYNC_BACKFILL_DAYS}
            step={1}
            value={daysInput}
            onChange={(e) => setDaysInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                submitBackfill();
              }
            }}
            aria-label="Backfill window in days back from today"
            aria-invalid={!daysValid}
            disabled={busy}
            className={cn(
              "h-8 w-16 rounded-md border bg-background px-2 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60",
              daysValid ? "border-border" : "border-destructive",
            )}
          />
          <span className="text-sm text-muted-foreground">days back</span>
          <button
            type="button"
            onClick={submitBackfill}
            disabled={busy || !daysValid}
            title={
              daysValid
                ? `One-time pull of the last ${parsedDays} days from TMS \u2014 runs the regular sync against a wider window. Useful for importing historical legacy-form and phone bookings or repopulating a newly-added column.`
                : `Enter a whole number of days from 1 to ${MAX_SYNC_BACKFILL_DAYS}.`
            }
            className={cn(
              "ml-auto inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition",
              busy || !daysValid
                ? "cursor-not-allowed bg-foreground/[0.06] text-muted-foreground/60"
                : "bg-primary/10 text-primary hover:bg-primary/15",
            )}
          >
            <History className={cn("h-4 w-4", isPending && "animate-spin")} />
            <span>
              {isPending
                ? "Syncing…"
                : daysValid
                  ? `Backfill ${parsedDays}d`
                  : "Backfill"}
            </span>
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          {BACKFILL_PRESETS.map((preset) => {
            const active = daysValid && parsedDays === preset;
            return (
              <button
                key={preset}
                type="button"
                onClick={() => setDaysInput(String(preset))}
                disabled={busy}
                className={cn(
                  "inline-flex h-6 items-center rounded-full px-2.5 text-xs font-medium transition",
                  active
                    ? "bg-primary/15 text-primary"
                    : "bg-foreground/[0.06] text-muted-foreground hover:bg-foreground/10",
                  busy && "cursor-not-allowed opacity-60",
                )}
              >
                {preset}d
              </button>
            );
          })}
          {!daysValid && (
            <span className="ml-auto text-xs text-destructive">
              Enter 1{"\u2013"}
              {MAX_SYNC_BACKFILL_DAYS} days
            </span>
          )}
        </div>
      </div>
      {/* Live status line — sits right under the backfill control (where
          the operator asked for it). While a pass runs it shows a spinner +
          "Sync in progress…" so a click that can't start a second run is
          self-explanatory; otherwise it shows the last finished pass's
          result. role=status + aria-live announce the transition to
          screen readers. */}
      <SyncStatusLine running={isSyncRunning} lastRun={lastRun} />
      {/* Re-sync a single trip by friendly id. Reconciles just that trip so
          a dispatcher's late service-type / mileage / status correction in
          TripLink lands without waiting for the 5-minute tick. This row is a
          plain (non-menu-item) container so the input is typeable —
          stopPropagation keeps Radix's menu typeahead from stealing
          keystrokes, and onSelect prevent-default would otherwise close the
          menu on click. */}
      <div
        className="flex items-center gap-2 px-3 py-2"
        onKeyDown={(e) => e.stopPropagation()}
      >
        <input
          type="text"
          value={tripId}
          onChange={(e) => setTripId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submitResync();
            }
          }}
          placeholder="Re-sync trip ID…"
          aria-label="Trip / friendly ID to re-sync"
          spellCheck={false}
          autoCapitalize="characters"
          disabled={isResyncing}
          className="h-8 w-full flex-1 rounded-md border border-border bg-background px-2 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
        />
        <button
          type="button"
          onClick={submitResync}
          disabled={isResyncing || tripId.trim().length === 0}
          title="Re-pull and reconcile just this trip from TripLink"
          className={cn(
            "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md px-2.5 text-sm font-medium transition",
            isResyncing || tripId.trim().length === 0
              ? "cursor-not-allowed bg-foreground/[0.06] text-muted-foreground/60"
              : "bg-primary/10 text-primary hover:bg-primary/15",
          )}
        >
          <Locate className={cn("h-4 w-4", isResyncing && "animate-spin")} />
          <span>{isResyncing ? "…" : "Re-sync"}</span>
        </button>
      </div>
    </>
  );
}

/** The section-level status line under "Backfill 60d". Shows a live
 *  "Sync in progress…" spinner while a pass runs (any source), otherwise
 *  a muted summary of the last finished pass ("Last sync 3m ago · 42
 *  seen · 1 new · 5 updated", with a "· N failed" suffix on a partial
 *  run). Renders nothing only when nothing's running AND no pass has ever
 *  finished. `role="status"` + `aria-live="polite"` announce the running
 *  → finished transition without stealing focus. */
function SyncStatusLine({
  running,
  lastRun,
}: {
  running: boolean;
  lastRun: AdminSyncTriplinkLastRun | null;
}) {
  if (!running && !lastRun) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="flex items-center gap-2 px-3 pb-1.5 pt-0.5 text-xs"
    >
      {running ? (
        <>
          <Loader2
            className="h-3.5 w-3.5 shrink-0 animate-spin text-primary"
            aria-hidden
          />
          <span className="font-medium text-foreground">Sync in progress…</span>
        </>
      ) : lastRun ? (
        <span
          className={cn(
            "text-muted-foreground",
            lastRun.outcome === "partial" && "text-amber-600 dark:text-amber-400",
          )}
        >
          Last sync {formatSyncAgo(lastRun.finishedAt)} · {lastRun.tripsSeen} seen
          {" · "}
          {lastRun.inserted} new · {lastRun.updated} updated
          {lastRun.failedDates > 0 ? ` · ${lastRun.failedDates} failed` : ""}
        </span>
      ) : null}
    </div>
  );
}

/** Compact "X ago" for the last-sync line. Self-contained on purpose —
 *  this internal module shouldn't reach into a page's local formatter. */
function formatSyncAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "recently";
  const secondsAgo = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secondsAgo < 45) return "just now";
  const minutesAgo = Math.round(secondsAgo / 60);
  if (minutesAgo < 60) return `${minutesAgo}m ago`;
  const hoursAgo = Math.round(minutesAgo / 60);
  if (hoursAgo < 24) return `${hoursAgo}h ago`;
  const daysAgo = Math.round(hoursAgo / 24);
  return `${daysAgo}d ago`;
}